import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canCreateMore, setCanCreateMore] = useState(true);

  useEffect(() => {
    // Check if user is authenticated on mount
    const token = localStorage.getItem('auth_token');
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await authAPI.me();
      setUser(response.data.user);
      setCanCreateMore(response.data.canCreateMore);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('auth_token');
    } finally {
      setLoading(false);
    }
  };

  const requestMagicLink = async (email, options = {}) => {
    const response = await authAPI.requestMagicLink(email, options);
    return response.data;
  };

  const verifyMagicLink = async (token) => {
    const response = await authAPI.verifyMagicLink(token);
    localStorage.setItem('auth_token', response.data.token);
    setUser(response.data.user);
    setCanCreateMore(true);
    return response.data;
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      setUser(null);
      setCanCreateMore(true);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        canCreateMore,
        requestMagicLink,
        verifyMagicLink,
        refreshUser: fetchUser,
        logout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
